import React from "react";

import { useAppSelector } from "@SonarCX/hooks/redux";
import { selectCustomFields } from "@SonarCX/slices/customFields";
import { selectUser } from "@SonarCX/slices/user";
import { formatValue, FormatValueTypes, getDisplayType } from "./helpers";

type UserUpdateProps = {
  type: string;
  oldValue: any;
  newValue: any;
  name: string;
};

enum ChangeTypes {
  SET_NEW,
  REMOVE,
  UPDATE,
}

const UserUpdate: React.FC<UserUpdateProps> = ({
  type,
  oldValue,
  newValue,
  name,
}) => {
  const { users } = useAppSelector(selectUser);
  const { customFields } = useAppSelector(selectCustomFields);

  const changeType = React.useMemo(() => {
    if (newValue && !oldValue) {
      return ChangeTypes.SET_NEW;
    }

    if (!newValue && oldValue) {
      return ChangeTypes.REMOVE;
    }

    return ChangeTypes.UPDATE;
  }, [oldValue, newValue]);

  const displayType = React.useMemo(
    () => getDisplayType({ type, customFields }),
    [type, customFields],
  );

  const [oldDisplayVal, newDisplayVal] = React.useMemo(() => {
    const data: Omit<FormatValueTypes, "val"> = {
      type,
      users,
      customFields,
    };
    const oldVal = formatValue({ val: oldValue, ...data });
    const newVal = formatValue({ val: newValue, ...data });

    return [oldVal, newVal];
  }, [oldValue, newValue, type, users, customFields]);

  if (displayType === null) {
    return null;
  }

  if (changeType === ChangeTypes.SET_NEW) {
    return (
      <>
        {name} <span className="emphasize">set</span> {displayType} to{" "}
        <span className="emphasize">{newDisplayVal}</span>
      </>
    );
  }

  if (changeType === ChangeTypes.REMOVE) {
    return (
      <>
        {name} <span className="emphasize">unset</span> {displayType}, was{" "}
        <span className="emphasize">{oldDisplayVal}</span>
      </>
    );
  }

  if (["assigneeIds", "watcherIds"].includes(type)) {
    if (!newDisplayVal || newDisplayVal.length === 0) {
      return (
        <>
          {name} <span className="emphasize">removed all</span> {displayType}
        </>
      );
    }

    return (
      <>
        {name} <span className="emphasize">set</span> {displayType} to{" "}
        <span className="emphasize">{newDisplayVal}</span>
      </>
    );
  }

  return (
    <>
      {name} <span className="emphasize">changed</span> {displayType} from{" "}
      <span className="emphasize">{oldDisplayVal}</span> to{" "}
      <span className="emphasize">{newDisplayVal}</span>
    </>
  );
};

export default UserUpdate;
